import React, { useEffect, useRef, useState } from "react";
import { Camera, Mic, MicOff, Square, Loader2, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_SECONDS = 180;
const DEFAULT_SECONDS = 60;

/**
 * Browser MediaRecorder dialog: captures `getDisplayMedia()` (screen) +
 * optional mic, stops at the chosen cap, uploads as WebM, returns the share
 * URL via `onComplete`.
 */
export default function WalkthroughRecorder({ open, onClose, onComplete }) {
  const [phase, setPhase] = useState("setup"); // setup | recording | uploading | done
  const [audio, setAudio] = useState(true);
  const [cap, setCap] = useState(DEFAULT_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  const [shareUrl, setShareUrl] = useState("");
  const [error, setError] = useState("");

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const stopRef = useRef(null);

  useEffect(() => {
    if (!open) reset();
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    stopAll();
    setPhase("setup");
    setElapsed(0);
    setShareUrl("");
    setError("");
    chunksRef.current = [];
  };

  const stopAll = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (stopRef.current) { clearTimeout(stopRef.current); stopRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    setError("");
    try {
      // Screen + system audio (Chrome supports system audio when checkbox ticked)
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: audio,
      });
      let combinedStream = display;
      if (audio) {
        // Prefer adding mic on top so narration is captured even without system-audio
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          const tracks = [...display.getTracks(), ...mic.getAudioTracks()];
          combinedStream = new MediaStream(tracks);
        } catch {
          // Mic blocked / not available — proceed with display-only
        }
      }
      streamRef.current = combinedStream;

      // Pick a widely-compatible mime type (Chrome+Edge support webm/vp9, Safari falls back)
      let mime = "video/webm;codecs=vp9,opus";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "video/webm;codecs=vp8,opus";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "video/webm";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "";
      const rec = new MediaRecorder(combinedStream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = handleStopped;
      // Auto-stop when the user revokes the screen share via the browser pill
      const vTrack = combinedStream.getVideoTracks()[0];
      if (vTrack) vTrack.onended = () => stopRecording();

      rec.start(1000); // 1s chunks
      setPhase("recording");
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= cap) { stopRecording(); return cap; }
          return s + 1;
        });
      }, 1000);
      stopRef.current = setTimeout(() => stopRecording(), (cap + 1) * 1000);
    } catch (e) {
      setError(e?.message || "Couldn't start screen capture. Did you allow screen sharing?");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (stopRef.current) { clearTimeout(stopRef.current); stopRef.current = null; }
  };

  const handleStopped = async () => {
    setPhase("uploading");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    try {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const fd = new FormData();
      fd.append("file", blob, "walkthrough.webm");
      fd.append("title", `DARA walkthrough · ${new Date().toLocaleString()}`);
      fd.append("duration_s", String(elapsed));
      fd.append("has_audio", String(audio));
      const r = await api.post("/walkthroughs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShareUrl(r.data.share_url);
      setPhase("done");
      toast.success("Walkthrough saved + emailed.");
      onComplete?.(r.data);
    } catch (e) {
      setPhase("setup");
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); }
    catch { toast.error("Couldn't copy — long-press the link to copy manually."); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center p-4" data-testid="walkthrough-recorder">
      <div className="bg-zinc-950 border border-zinc-800 max-w-xl w-full">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#FFB000]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFB000]">// TRY DARA LIVE — WALKTHROUGH RECORDER</span>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="walkthrough-close-btn">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-6 space-y-4">
          {phase === "setup" && (
            <>
              <h2 className="font-heading font-black text-2xl tracking-tight">Capture a 60-180s screencast.</h2>
              <p className="text-sm text-zinc-400 leading-relaxed">
                You&apos;ll be prompted to pick a tab or window. We&apos;ll bundle it with your narration into a private link
                you can share with your board, CFO, or revenue team. The DARA sales team also gets pinged so we can follow up
                with a tailored deployment plan.
              </p>

              <div className="border border-zinc-800 p-4 space-y-3">
                <label className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">LENGTH CAP</div>
                    <div className="font-heading font-bold text-lg">{cap}s</div>
                  </div>
                  <input
                    type="range" min={30} max={MAX_SECONDS} step={15} value={cap}
                    onChange={(e) => setCap(Number(e.target.value))}
                    className="w-44 accent-[#FFB000]"
                    data-testid="walkthrough-cap-slider"
                  />
                </label>

                <label className="flex items-center gap-3 pt-2 border-t border-zinc-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={audio}
                    onChange={(e) => setAudio(e.target.checked)}
                    className="accent-[#FFB000] w-4 h-4"
                    data-testid="walkthrough-audio-toggle"
                  />
                  {audio ? <Mic className="w-3.5 h-3.5 text-[#00E676]" /> : <MicOff className="w-3.5 h-3.5 text-zinc-600" />}
                  <span className="font-mono text-[11px]">{audio ? "Record narration (mic)" : "Screen only — no audio"}</span>
                </label>
              </div>

              {error && (
                <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF3B30] font-mono text-[11px] p-3 flex gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900" data-testid="walkthrough-cancel-btn">Cancel</button>
                <button onClick={start} className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00] flex items-center gap-2" data-testid="walkthrough-start-btn">
                  <Camera className="w-3.5 h-3.5" /> START RECORDING
                </button>
              </div>
            </>
          )}

          {phase === "recording" && (
            <>
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-[#FF3B30] animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">REC</span>
                <span className="font-heading font-black text-3xl tabular-nums">{String(Math.floor(elapsed/60)).padStart(2,'0')}:{String(elapsed%60).padStart(2,'0')}</span>
                <span className="font-mono text-[11px] text-zinc-500">/ {String(Math.floor(cap/60)).padStart(2,'0')}:{String(cap%60).padStart(2,'0')}</span>
              </div>
              <div className="w-full h-1 bg-zinc-900">
                <div className="h-1 bg-[#FFB000] transition-all" style={{ width: `${Math.min(100, (elapsed/cap)*100)}%` }} />
              </div>
              <p className="text-sm text-zinc-400">Walk us through your goal — DARA captures the picture-in-picture preview at the bottom. Click STOP when done; we&apos;ll auto-stop at the cap.</p>
              <div className="flex justify-end pt-2">
                <button onClick={stopRecording} className="bg-[#FF3B30] text-white font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E5302A] flex items-center gap-2" data-testid="walkthrough-stop-btn">
                  <Square className="w-3.5 h-3.5" /> STOP & UPLOAD
                </button>
              </div>
            </>
          )}

          {phase === "uploading" && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-7 h-7 animate-spin text-[#FFB000]" />
              <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Encoding & uploading…</div>
              <div className="text-xs text-zinc-600">{elapsed}s of footage being saved to your workspace.</div>
            </div>
          )}

          {phase === "done" && shareUrl && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[#00E676] font-mono text-[11px] uppercase tracking-widest">
                <CheckCircle2 className="w-4 h-4" /> Walkthrough saved · email sent
              </div>
              <div className="border border-zinc-800 p-3 break-all font-mono text-[11px] text-zinc-300" data-testid="walkthrough-share-url">{shareUrl}</div>
              <div className="flex justify-end gap-2">
                <button onClick={copyLink} className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900" data-testid="walkthrough-copy-btn">COPY LINK</button>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00]" data-testid="walkthrough-open-btn">OPEN IN NEW TAB</a>
                <button onClick={onClose} className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900">CLOSE</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
